import React, { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { GlobalContext } from "../../context";
import LoadingSpinner from "../LoadingSpinner";
import CourseBlackboard from "./CourseBlackboard";
import { currencyFormatter } from "../../utility";

function BrowseCourseCard({ title, images, image_url, price, price_ngn, course_level_id, course_type_id, uuid, is_free, enrolledUuids }) {
  const navigate = useNavigate();
  const isEnrolled = enrolledUuids.includes(uuid);

  const imgSrc = image_url || (Array.isArray(images) ? images[0] : images);
  // parseFloat: currencyFormatter rejects non-number types and returns 0
  const displayPrice = parseFloat(price_ngn || price || 0);
  // course_type_id 1 = Live, 2 = Online
  const courseRoute = Number(course_type_id) === 1
    ? `/live-course/${uuid}`
    : `/online-course/${uuid}`;

  return (
    <div
      onClick={() => navigate(courseRoute)}
      className="bg-white rounded-2xl border border-gray-100 overflow-hidden flex flex-col hover:shadow-md transition-shadow duration-200 cursor-pointer"
    >
      <div className="relative h-44 bg-gradient-to-br from-[#d15424]/10 to-[#294a93]/10 overflow-hidden flex-shrink-0">
        {imgSrc ? (
          <img src={imgSrc} alt={title} className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-5xl">📖</div>
        )}
        {is_free === 1 && (
          <span className="absolute top-2 right-2 bg-blue-500 text-white text-xs px-2 py-1 rounded-full font-medium">
            Free
          </span>
        )}
        {isEnrolled && (
          <span className="absolute top-2 left-2 bg-green-500 text-white text-xs px-2 py-1 rounded-full font-medium">
            Enrolled
          </span>
        )}
      </div>

      <div className="p-4 flex flex-col flex-1">
        {course_level_id && (
          <span className="text-xs text-[#d15424] font-medium uppercase tracking-wide mb-1">
            {course_level_id}
          </span>
        )}
        <h3 className="font-semibold text-sm text-gray-800 mb-3 flex-1 line-clamp-2">{title}</h3>
        <div className="flex items-center justify-between pt-3 border-t border-gray-100">
          <span className="font-bold text-gray-800 text-sm">
            {is_free === 1 ? "Free" : currencyFormatter(displayPrice, { currency: "NGN" })}
          </span>
          <button
            className="text-xs px-3 py-2 rounded-xl bg-[#d15424] text-white font-medium hover:bg-[#b8451d] transition-colors"
          >
            Learn More
          </button>
        </div>
      </div>
    </div>
  );
}

export default function MyCourse({ targetRef, type }) {
  const { userCoursesState, onlineCoursesState, liveCoursesState, authState } = useContext(GlobalContext);
  const isInstructor = authState?.data?.user?.is_instructor;
  const [activeTab, setActiveTab] = useState(isInstructor ? "browse" : "mine");

  useEffect(() => {
    if (type === "live" || type === "online") {
      setActiveTab(isInstructor ? "browse" : "mine");
    }
  }, [type, isInstructor]);

  const courses = userCoursesState?.data || [];
  const enrolledUuids = courses.map((c) => c.uuid).filter(Boolean);

  // Combine online + live available courses
  const onlineCourses = Array.isArray(onlineCoursesState?.data) ? onlineCoursesState.data : [];
  const liveCourses = Array.isArray(liveCoursesState?.data) ? liveCoursesState.data : [];
  const availableCourses = [...onlineCourses, ...liveCourses];

  const browsing = onlineCoursesState?.loading || liveCoursesState?.loading;

  return (
    <div style={{ minHeight: "20vh" }}>
      <h1 className="text-2xl font-bold text-gray-800 mb-6">Courses</h1>

      {/* Tabs */}
      {!isInstructor && (
        <div className="flex gap-0 mb-6 border-b border-gray-200">
          {[
            { id: "mine", label: "My Courses" },
            { id: "browse", label: "Browse Courses" },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-5 py-3 text-sm font-medium border-b-2 transition-colors -mb-px ${
                activeTab === tab.id
                  ? "border-[#d15424] text-[#d15424]"
                  : "border-transparent text-gray-500 hover:text-gray-700"
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      )}

      {/* My Courses — same card design as the overview */}
      {activeTab === "mine" && (
        <CourseBlackboard />
      )}

      {/* Browse Courses */}
      {activeTab === "browse" && (
        <>
          {browsing && availableCourses.length === 0 ? (
            <LoadingSpinner />
          ) : availableCourses.length === 0 ? (
            <div className="text-center py-16">
              <div className="text-5xl mb-3">🎓</div>
              <p className="text-gray-500 text-sm">No courses available at the moment.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
              {availableCourses.map((course, i) => (
                <BrowseCourseCard
                  key={course.uuid || i}
                  {...course}
                  enrolledUuids={enrolledUuids}
                />
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
